import React from "react";
import { Card } from "../ui/card";
import { Progress } from "../ui/progress";
import { Button } from "../ui/button";
import { Flame, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface NutritionData {
  calories: { current: number; target: number };
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
}

interface NutritionSummaryCardProps {
  nutrition: NutritionData;
}

const NutritionSummaryCard: React.FC<NutritionSummaryCardProps> = ({
  nutrition,
}) => {
  const navigate = useNavigate();
  const calorieProgress =
    (nutrition.calories.current / nutrition.calories.target) * 100;

  return (
    <Card
      className="cursor-pointer rounded-xl border-2 border-border bg-card p-5 shadow-md transition-transform hover:scale-[1.02]"
      onClick={() => navigate("/glucera/mealpage")}
    >
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-base font-semibold text-foreground">
          Today&apos;s Nutrition
        </h3>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          View All <ChevronRight className="ml-0.5 h-4 w-4" />
        </Button>
      </div>

      {/* Calories Section */}
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg border-2 border-orange-300 bg-orange-100">
          <Flame className="h-5 w-5 text-orange-500" />
        </div>
        <div className="flex-1">
          <div className="mb-1 flex items-baseline gap-1">
            <span className="text-xl font-bold text-foreground">
              {Math.round(nutrition.calories.current)}
            </span>
            <span className="text-sm text-muted-foreground">
              / {Math.round(nutrition.calories.target)} cal
            </span>
            <span className="ml-auto text-sm font-medium text-foreground">
              {Math.round(calorieProgress)}%
            </span>
          </div>
          <p className="mb-2 text-xs text-muted-foreground">Calories Intake</p>
          <Progress value={calorieProgress} className="h-2" />
        </div>
      </div>

      {/* Nutrition Metrics Grid */}
      <div className="grid grid-cols-4 gap-2">
        <div className="text-center">
          <div className="text-base font-bold text-blue-500">
            {Math.round(nutrition.protein)}g
          </div>
          <div className="text-xs text-muted-foreground">Protein</div>
        </div>

        <div className="text-center">
          <div className="text-base font-bold text-orange-500">
            {Math.round(nutrition.carbs)}g
          </div>
          <div className="text-xs text-muted-foreground">Carbs</div>
        </div>

        <div className="text-center">
          <div className="text-base font-bold text-yellow-500">
            {Math.round(nutrition.fat)}g
          </div>
          <div className="text-xs text-muted-foreground">Fat</div>
        </div>

        <div className="text-center">
          <div className="text-base font-bold text-green-500">
            {Math.round(nutrition.fiber)}g
          </div>
          <div className="text-xs text-muted-foreground">Fiber</div>
        </div>
      </div>
    </Card>
  );
};

export default NutritionSummaryCard;
